import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ColorResult } from '@uiw/react-color';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { SelectGroupOption } from '../../general-components/select-group';
import SelectSmall from '../../general-components/select-small';
import ColorPicker from './color-pickers/color-picker';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import TitleCard from '../../general-components/title-card';
import { GreenroomState } from '../../../stores/types/types';
import type { GreenroomUser } from '../../../stores/types/session';
import { formatHex } from '../../../utils/utils';
import type { AppSettings } from '../../../stores/types/settings';

interface Props {
	settingsForShotEdited: (newQueue: any) => void;
	appliedMatchesQueue: boolean;
	queue: AppSettings;
}

const animationOptions: SelectGroupOption[] = [
	{ label: 'Dissolve', value: 'dissolve' },
	{ label: 'Move In', value: 'move-in' },
	{ label: 'None', value: 'none' },
];

const durationOptions: SelectGroupOption[] = [
	{ label: 'All Time', value: null },
	{ label: '5 sec.', value: 5 },
	{ label: '10 sec.', value: 10 },
];

export const titleDefault: GreenroomUser = {
	first_name: 'Name',
	last_name: '',
	location: 'Location',
	title: 'Title',
	avatar: '',
	gr_user: 0,
	email: '',
	bg_image: '',
	tutorial_video_count: 0,
};

export default function Titles(props: Props) {
	const { queue } = props;
	const { appliedMatchesQueue, settingsForShotEdited } = props;
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const colorDebounce = useRef<NodeJS.Timeout | null>(null);
	const [font1, setFont1] = useState<any>({ hex: queue.titlesFontColor1 });
	const [font2, setFont2] = useState<any>({ hex: queue.titlesFontColor2 });
	const [bg1, setBg1] = useState<any>({ hex: queue.titlesBackgroundColor1 });
	const [bg2, setBg2] = useState<any>({ hex: queue.titlesBackgroundColor2 });
	const [bg3, setBg3] = useState<any>({ hex: queue.titlesBackgroundColor3 });

	const [svgDropdownVisible, setSvgDropdownVisible] = useState(false);
	const [activeColorPicker, setActiveColorPicker] = useState<string | null>(null);
	const [colorPickerPosition, setColorPickerPosition] = useState({ top:0,right:0});

	const selectedAnimation = animationOptions.find(
		(option) => option.value === queue.titlesAnimation,
	);
	const selectedDuration = durationOptions.find((option) => option.value === queue.titlesDuration);

	const exampleUser = user ? { ...user } : titleDefault;

	function toggleSvgDropdownVisible() {
		setSvgDropdownVisible(!svgDropdownVisible);
	}

	useEffect(() => {
		if (appliedMatchesQueue) {
			setFont1({ hex: queue.titlesFontColor1 });
			setFont2({ hex: queue.titlesFontColor2 });
			setBg1({ hex: queue.titlesBackgroundColor1 });
			setBg2({ hex: queue.titlesBackgroundColor2 });
			setBg3({ hex: queue.titlesBackgroundColor3 });
		}
	}, [appliedMatchesQueue]);

	function openColorPicker(colorPicker: string) {
		// Set unique positions for each color picker
		const positions = {
			font1: { top:40.5,right:0},
			font2: { top:88.5,right:0},
			background1: { top:165.5,right:0},
			background2: { top:213.5,right:0},
			background3: { top:-30.5,right:0},
		};

		setColorPickerPosition(positions[colorPicker as keyof typeof positions] || { top:0,right:0});
		setActiveColorPicker(colorPicker);
	}

	function closeColorPicker() {
		setActiveColorPicker(null);
	}

	function getCurrentColor() {
		switch (activeColorPicker) {
			case 'font1': return font1;
			case 'font2': return font2;
			case 'background1': return bg1;
			case 'background2': return bg2;
			case 'background3': return bg3;
			default: return { hex: '#FFFFFF' };
		}
	}

	function getCurrentChangeHandler() {
		switch (activeColorPicker) {
			case 'font1': return changeFont1;
			case 'font2': return changeFont2;
			case 'background1': return changeBG1;
			case 'background2': return changeBG2;
			case 'background3': return changeBG3;
			default: return () => {};
		}
	}

	function handleCheckbox(type: string) {
		const newQueue = { ...queue };
		switch (type) {
			case 'location':
				newQueue.titlesUseLocation = !queue.titlesUseLocation;
				settingsForShotEdited(newQueue);
				break;
			case 'name':
				newQueue.titlesUseName = !queue.titlesUseName;
				settingsForShotEdited(newQueue);
				break;
			case 'title':
				newQueue.titlesUseTitle = !queue.titlesUseTitle;
				settingsForShotEdited(newQueue);
				break;
			default: break;
		}
	}

	function changeSelectedTitleCard(cardType: number) {
		setSvgDropdownVisible(!svgDropdownVisible);
		const newQueue = { ...queue, titlesSelectedCard: cardType };
		settingsForShotEdited(newQueue);
	}

	function changeFont1(color: ColorResult) {
		setFont1(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesFontColor1: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeFont2(color: ColorResult) {
		setFont2(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesFontColor2: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeBG1(color: ColorResult) {
		setBg1(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesBackgroundColor1: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeBG2(color: ColorResult) {
		setBg2(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesBackgroundColor2: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeBG3(color: ColorResult) {
		setBg3(color);

		if (colorDebounce.current) { clearTimeout(colorDebounce.current); }

		colorDebounce.current = setTimeout(() => {
			const newQueue = { ...queue, titlesBackgroundColor3: color.hex };
			settingsForShotEdited(newQueue);
		}, 100);
	}

	function changeTitlesAnimation(animation: String) {
		const newQueue = { ...queue, titlesAnimation: animation };
		settingsForShotEdited(newQueue);
	}

	function changeTitlesDuration(duration: Number) {
		const newQueue = { ...queue, titlesDuration: duration };
		settingsForShotEdited(newQueue);
	}

	function changeTitlesPosition(position: string) {
		const newQueue = { ...queue, titlesPosition: position };
		settingsForShotEdited(newQueue);
	}

	return (
		<>
			<div className="settings-container titles-container">
				<div className={classNames('titles-options')}>

					<label className="section-title">STYLE</label>

					<div className="title-checkboxes">
						<div className="checkbox-settings-wrapper">
							<div className={classNames('checkbox-settings', { selected: queue.titlesUseName })} onClick={() => { handleCheckbox('name'); }}>
								<div className="checkbox-settings-outer">
									<FontAwesomeIcon icon={faCheck} />
								</div>
								<label>Name</label>
							</div>
						</div>
						<div className="checkbox-settings-wrapper">
							<div className={classNames('checkbox-settings', { selected: queue.titlesUseTitle })} onClick={() => { handleCheckbox('title'); }}>
								<div className="checkbox-settings-outer">
									<FontAwesomeIcon icon={faCheck} />
								</div>
								<label>Title</label>
							</div>
						</div>
						<div className="checkbox-settings-wrapper">
							<div className={classNames('checkbox-settings', { selected: queue.titlesUseLocation })} onClick={() => { handleCheckbox('location'); }}>
								<div className="checkbox-settings-outer">
									<FontAwesomeIcon icon={faCheck} />
								</div>
								<label>Location</label>
							</div>
						</div>
					</div>

					<div className="titles-dropdown-wrapper">
						<div className="titles-dropdown" onClick={toggleSvgDropdownVisible}>
							<div className="titles-dropdown-option preview">
								<TitleCard cardType={queue.titlesSelectedCard} user={exampleUser} preview={true} />
								<span className="arrow"><Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={COLORS.DEFAULT_GRAY} size={10} /></span>
							</div>
						</div>

						{svgDropdownVisible && (
							<>
								<div className="titles-dropdown-overlay" onClick={toggleSvgDropdownVisible}></div>
								<div className="titles-dropdown-active">
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(1)}>
										<TitleCard cardType={1} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(2)}>
										<TitleCard cardType={2} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(3)}>
										<TitleCard cardType={3} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(4)}>
										<TitleCard cardType={4} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(5)}>
										<TitleCard cardType={5} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(6)}>
										<TitleCard cardType={6} user={exampleUser} preview={true} />
									</div>
									<div className="titles-dropdown-option" onClick={() => changeSelectedTitleCard(7)}>
										<TitleCard cardType={7} user={exampleUser} preview={true} />
									</div>
								</div>
							</>
						)}
					</div>

					<div id='color-picker-titles-portal' className='color-portal portal-right-aligned margin-right-small'/>

					<div className="titles-row">
						<label className='titles-row-group'>Font colors</label>
						<div className={classNames('color-toggle', { editing: activeColorPicker === 'font1' })} onClick={() => openColorPicker('font1')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesFontColor1 }}></div>
							<span className='format-hex'>{queue.titlesFontColor1}</span>
						</div>
					</div>
					<div className="titles-row">
						<label></label>
						<div className={classNames('color-toggle', { editing: activeColorPicker === 'font2' })} onClick={() => openColorPicker('font2')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesFontColor2 }}></div>
							<span className='format-hex'>{queue.titlesFontColor2}</span>
						</div>
					</div>

					<hr />

					<div className="titles-row">
						<label className='titles-row-group'>Background colors</label>

						<div className={classNames('color-toggle', { editing: activeColorPicker === 'background1' })} onClick={() => openColorPicker('background1')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesBackgroundColor1 }}></div>
							<span className='format-hex'>{queue.titlesBackgroundColor1}</span>
						</div>
					</div>
					<div className="titles-row">
						<label></label>
						<div className={classNames('color-toggle', { editing: activeColorPicker === 'background2' })} onClick={() => openColorPicker('background2')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesBackgroundColor2 }}></div>
							<span className='format-hex'>{queue.titlesBackgroundColor2}</span>
						</div>
					</div>
					<div className="titles-row">
						<label></label>
						<div className={classNames('color-toggle', { editing: activeColorPicker === 'background3' })} onClick={() => openColorPicker('background3')}>
							<div className="color-swatch" style={{ backgroundColor: queue.titlesBackgroundColor3 }}></div>
							<span className='format-hex'>{queue.titlesBackgroundColor3}</span>
						</div>
					</div>

					<ColorPicker
						open={activeColorPicker !== null}
						openHexAtStart={formatHex(getCurrentColor().hex)}
						onChange={getCurrentChangeHandler()}
						onClose={closeColorPicker}
						portalId='color-picker-titles-portal'
						customPosition={colorPickerPosition}
					/>

					<hr />

					<div className="titles-row">
						<label className='titles-row-group'>Animation</label>
						<SelectSmall
							options={animationOptions}
							label={selectedAnimation?.label || animationOptions[0].label}
							selected={selectedAnimation || animationOptions[0]}
							dropdown={true}
							onChange={changeTitlesAnimation}
							dropdownPosition={'drop-left'}
						/>
					</div>
					<div className="titles-row">
						<label className='titles-row-group'>Visible for</label>
						<SelectSmall
							options={durationOptions}
							label={selectedDuration?.label || durationOptions[0].label}
							selected={selectedDuration || durationOptions[0]}
							dropdown={true}
							onChange={changeTitlesDuration}
							dropdownPosition={'drop-left'}
						/>
					</div>

					{(queue.titlesSelectedCard === 3) && <>
						<hr />

						<div className="titles-row">
							<label>Position</label>

							<div className="corner-markers">
								<div className={classNames('corner-marker bottom-left', { active: queue.titlesPosition === 'bottom-left' })} onClick={() => changeTitlesPosition('bottom-left')}>
									<span>&#9698;</span>
								</div>
								<div className={classNames('corner-marker top-left', { active: queue.titlesPosition === 'top-left' })} onClick={() => changeTitlesPosition('top-left')}>
									<span>&#9698;</span>
								</div>
								<div className={classNames('corner-marker top-right', { active: queue.titlesPosition === 'top-right' })} onClick={() => changeTitlesPosition('top-right')}>
									<span>&#9698;</span>
								</div>
								<div className={classNames('corner-marker bottom-right', { active: queue.titlesPosition === 'bottom-right' })} onClick={() => changeTitlesPosition('bottom-right')}>
									<span>&#9698;</span>
								</div>
							</div>
						</div>
					</>}

				</div>
			</div>
		</>
	);
}